import { useEffect, useMemo, useState } from 'react';
import { useMount, useUpdateEffect } from 'react-use';
import { analyticsApi } from 'apis/analytics';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { analyticsActions } from '../../../../analytics.actions';
export const useDashboardInit = () => {
    const navigate = useAppNavigate();
    const dispatch = useAppDispatch();
    const params = useAppPathParams('/analytics/dashboard/:slug');
    const { dashboard } = useAppSelector((state) => state.analytics);
    const [isInitialized, setIsInitialized] = useState(false);
    /** The list of dashboards from the store. */
    const dashboards = analyticsApi.endpoints.readDashboards.useQueryState(undefined, {
        selectFromResult: ({ data }) => { var _a; return (_a = data === null || data === void 0 ? void 0 : data.data.dashboards) !== null && _a !== void 0 ? _a : []; },
    });
    // Handles the case where the dashboard slug in the URL path params is not found.
    // This can happen when accessing the page via a link to the root /analytics path.
    // In this case, we redirect to dashboard from the Redux store if any or the first in the list.
    useMount(() => {
        if (dashboards.length && !params.slug) {
            const initialDashboard = dashboard !== null && dashboard !== void 0 ? dashboard : dashboards[0];
            navigate({
                path: '/analytics/dashboard/:slug',
                params: {
                    path: {
                        slug: initialDashboard.slug,
                    },
                },
            }, { replace: true });
        }
    });
    // Updates the cached dashboard in the Redux store when the URL is changed.
    // This also runs immediately after the previous effect that handles the missing URL path param.
    useEffect(() => {
        const nextDashboard = dashboards.find(({ slug }) => slug === params.slug);
        if (nextDashboard) {
            setIsInitialized(false);
            dispatch(analyticsActions.setDashboard({ dashboard: nextDashboard }));
        }
        // Only run this effect when the URL params change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.slug]);
    // Updates the init status when the dashboard is set in the Redux store.
    useUpdateEffect(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.slug) === params.slug) {
            setIsInitialized(true);
        }
    }, [dashboard, params.slug]);
    return useMemo(() => ({
        dashboard,
        isInitialized,
    }), [dashboard, isInitialized]);
};
